import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const MenuContext = createContext();

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
};

// Configuración de menús con permisos
const menuConfiguration = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: '🏠',
    path: '/dashboard',
    requiredPermissions: [],
    submenus: []
  },
  {
    id: 'descarga-masiva',
    label: 'Descarga Masiva',
    icon: '📥',
    requiredPermissions: ['descarga_masiva'],
    submenus: [
      {
        id: 'dm-clientes',
        label: 'Clientes',
        path: '/descarga-masiva/clientes',
        icon: '🏢',
        requiredPermissions: ['descarga_masiva']
      },
      {
        id: 'dm-descargas',
        label: 'Descargas',
        path: '/descarga-masiva/descargas',
        icon: '📊',
        requiredPermissions: ['descarga_masiva']
      },
      {
        id: 'dm-consumos',
        label: 'Consumos',
        path: '/descarga-masiva/consumos',
        icon: '📋',
        requiredPermissions: ['descarga_masiva']
      }
    ]
  },
  {
    id: 'facturacion',
    label: 'Facturación',
    icon: '🧾',
    requiredPermissions: ['facturacion'],
    submenus: [
      {
        id: 'fact-nueva',
        label: 'Nueva Factura',
        path: '/facturacion/nueva',
        icon: '➕',
        requiredPermissions: ['facturacion_crear']
      },
      {
        id: 'fact-clientes',
        label: 'Clientes',
        path: '/facturacion/clientes',
        icon: '👥',
        requiredPermissions: ['facturacion']
      },
      {
        id: 'fact-config',
        label: 'Configuración',
        path: '/facturacion/configuracion',
        icon: '⚙️',
        requiredPermissions: ['facturacion_admin']
      }
    ]
  },
  {
    id: 'admin',
    label: 'Administración',
    icon: '👨‍💼',
    requiredPermissions: ['admin'],
    submenus: [
      {
        id: 'admin-usuarios',
        label: 'Usuarios',
        path: '/admin/usuarios',
        icon: '👥',
        requiredPermissions: ['super_admin']
      },
      {
        id: 'admin-reportes',
        label: 'Reportes',
        path: '/admin/reportes',
        icon: '📈',
        requiredPermissions: ['admin']
      }
    ]
  }
];

// Matriz de permisos por rol
const permissionMatrix = {
  'descarga_masiva': ['Usuario', 'Admin', 'SuperAdmin'],
  'facturacion': ['Usuario', 'Admin', 'SuperAdmin'],
  'facturacion_crear': ['Usuario', 'Admin', 'SuperAdmin'],
  'facturacion_admin': ['Admin', 'SuperAdmin'],
  'admin': ['Admin', 'SuperAdmin'],
  'super_admin': ['SuperAdmin']
};

export const MenuProvider = ({ children }) => {
  const { user } = useAuth();
  const [activeMenu, setActiveMenu] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Detectar si es móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Función para verificar permisos
  const hasPermission = (requiredPermission) => {
    if (!requiredPermission || requiredPermission.length === 0) return true;
    if (!user?.role) return false;
    
    return permissionMatrix[requiredPermission]?.includes(user.role) || false;
  };

  // Obtener menús filtrados por permisos
  const getFilteredMenus = () => {
    return menuConfiguration.filter(menu => {
      // Verificar permisos del menú principal
      const hasMenuPermission = menu.requiredPermissions.length === 0 || 
        menu.requiredPermissions.some(permission => hasPermission(permission));
      
      if (!hasMenuPermission) return false;

      // Filtrar submenús por permisos
      if (menu.submenus && menu.submenus.length > 0) {
        menu.submenus = menu.submenus.filter(submenu => 
          submenu.requiredPermissions.length === 0 || 
          submenu.requiredPermissions.some(permission => hasPermission(permission))
        );
      }

      return true;
    });
  };

  // Función para determinar el menú activo basado en la ruta actual
  const determineActiveMenu = (pathname) => {
    for (const menu of menuConfiguration) {
      // Verificar si es un menú simple (sin submenús)
      if (menu.path && pathname === menu.path) {
        return { menu: menu.id, submenu: null };
      }

      // Verificar submenús
      if (menu.submenus) {
        for (const submenu of menu.submenus) {
          if (pathname === submenu.path || pathname.startsWith(submenu.path + '/')) {
            return { menu: menu.id, submenu: submenu.id };
          }
        }
      }

      // Verificar si la ruta pertenece al módulo (para rutas no exactas)
      if (pathname.startsWith(`/${menu.id}`)) {
        return { menu: menu.id, submenu: null };
      }
    }

    return { menu: null, submenu: null };
  };

  // Función para manejar la navegación
  const handleMenuClick = (menuId) => {
    console.log('MenuContext handleMenuClick called with:', menuId);
    console.log('Current activeMenu:', activeMenu);
    console.log('Is mobile:', isMobile);
    
    if (isMobile) {
      // En móvil, toggle del menú
      const newActiveMenu = activeMenu === menuId ? null : menuId;
      setActiveMenu(newActiveMenu);
      console.log('Setting activeMenu to:', newActiveMenu);
    } else {
      // En desktop, también toggle pero con comportamiento hover
      const newActiveMenu = activeMenu === menuId ? null : menuId;
      setActiveMenu(newActiveMenu);
      console.log('Setting activeMenu to:', newActiveMenu);
    }
  };

  // Función para cerrar menús
  const closeMenus = () => {
    console.log('Closing all menus');
    setActiveMenu(null);
    setActiveSubmenu(null);
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  // Función para manejar navegación y cerrar menús
  const handleNavigation = (path) => {
    console.log('Navigating to:', path);
    closeMenus();
  };

  // Función para toggle del menú móvil
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const contextValue = {
    // Estado
    activeMenu,
    activeSubmenu,
    isMobile,
    mobileMenuOpen,
    
    // Funciones de estado
    setActiveMenu,
    setActiveSubmenu,
    setMobileMenuOpen,
    
    // Configuración y permisos
    menuConfig: getFilteredMenus(),
    userPermissions: user?.permissions || [],
    hasPermission,
    
    // Funciones de navegación
    handleMenuClick,
    closeMenus,
    toggleMobileMenu,
    determineActiveMenu,
    handleNavigation,
    
    // Datos del usuario
    user
  };

  return (
    <MenuContext.Provider value={contextValue}>
      {children}
    </MenuContext.Provider>
  );
};

export default MenuProvider;